import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import PrelitXLogo from '@/components/PrelitXLogo';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    firm_name: ''
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const user = await register(formData.name, formData.email, formData.password, formData.firm_name);
      
      // Show welcome message with signup bonus
      toast.success('You have received 1 free credit. Welcome to Prelit X!', {
        duration: 5000,
        style: {
          backgroundColor: '#FFFFFF',
          color: '#000000',
          border: '2px solid #D4AF37'
        }
      });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container min-h-screen flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md shadow-2xl border-0 glass-card">
        <CardHeader className="text-center space-y-4 pb-6">
          <div className="mx-auto mb-2" style={{ marginTop: '24px' }}>
            <PrelitXLogo size="large" className="justify-center" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold" style={{ color: '#245451' }}>
              Create Your Account
            </CardTitle>
            <CardDescription className="mt-2" style={{ color: '#64748B' }}>
              Start generating medical chronologies with 3 free credits
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" style={{ color: '#1A1A1A', fontWeight: 500 }}>Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: '#1A1A1A', fontWeight: 500 }}>Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="attorney@lawfirm.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="firm_name" style={{ color: '#1A1A1A', fontWeight: 500 }}>Firm Name <span style={{ color: '#64748B', fontWeight: 400 }}>(Optional)</span></Label>
              <Input
                id="firm_name"
                type="text"
                placeholder="Smith & Associates"
                value={formData.firm_name}
                onChange={(e) => setFormData({ ...formData, firm_name: e.target.value })}
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="register-firm-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: '#1A1A1A', fontWeight: 500 }}>Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="register-password-input"
              />
              <p className="text-xs mt-1" style={{ color: '#64748B' }}>
                Must be at least 8 characters
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" style={{ color: '#1A1A1A', fontWeight: 500 }}>Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                minLength={8}
                className="h-11"
                style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                data-testid="register-confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 font-medium mt-6"
              style={{ backgroundColor: '#245451' }}
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm" style={{ color: '#64748B' }}>
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="font-medium"
              style={{ color: '#406962' }}
            >
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
